import { Button } from 'reactstrap';
import { ColumnDef } from '@tanstack/react-table';
import TableContainer from './../../Campaign/components/TableContainer';
import { NoDataCard } from '~/containers/Campaign/components/NoDataCard';
import { renderIcon } from '~/shared/components/icons';
import { FundRequestVendor } from '~/types/fundrequests';
import { useHistory } from 'react-router-dom';
import { toUSD } from '~/shared/utils';
import { MdCheck, MdDelete } from 'react-icons/md';
import { FaExclamationCircle } from 'react-icons/fa';
import { deleteVendor } from '~/services/fundrequests';
import { ConfirmModal } from '~/shared/components/ConfirmModal';
import { useState } from 'react';

type PayeesTableProps = {
    payees: FundRequestVendor[];
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    pageSize: number;
    setPageSize: (size: number) => void;
    isLoading: boolean;
    onDelete?: (vendorId: number) => Promise<void>;
};

function PayeesTable({
    payees,
    currentPage,
    totalPages,
    onPageChange,
    pageSize,
    setPageSize,
    isLoading,
    onDelete,
}: PayeesTableProps) {
    const history = useHistory();
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [vendorToDelete, setVendorToDelete] =
        useState<FundRequestVendor | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleDeleteClick = (vendor: FundRequestVendor) => {
        setVendorToDelete(vendor);
        setIsDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!vendorToDelete || !onDelete) return;

        setIsDeleting(true);
        try {
            await onDelete(vendorToDelete.id);
            setIsDeleteModalOpen(false);
            setVendorToDelete(null);
        } catch (error) {
            console.error('Error deleting vendor:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDeleteCancel = () => {
        if (!isDeleting) {
            setIsDeleteModalOpen(false);
            setVendorToDelete(null);
        }
    };

    const columns: ColumnDef<FundRequestVendor>[] = [
        {
            id: 'payee',
            header: 'Payee',
            size: 0,
            maxSize: 300,
            accessorFn: (props) => {
                return (
                    <div>
                        <div
                            className='text-primary fw-bold'
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                                history.push(`/payees/details/${props.id}`);
                            }}
                        >
                            {props.name} {renderIcon('edit_default')}
                        </div>
                    </div>
                );
            },
        },
        {
            id: 'verified',
            header: 'Verified',
            accessorFn: (props) => {
                return (
                    <div>
                        {props.verified_in_mip ? (
                            <MdCheck color='#01aeb4' />
                        ) : (
                            ''
                        )}
                    </div>
                );
            },
        },
        {
            id: 'ach',
            header: 'ACH',
            accessorFn: (props) => {
                const has_needs_ach = props.fund_requests.some(
                    (f) => f.needs_ach_info === true
                );
                return (
                    <div>
                        {props.payment_type === 'ACH' && props.ach_last_4 ? (
                            <MdCheck color='#01aeb4' />
                        ) : has_needs_ach ? (
                            <FaExclamationCircle color='red' />
                        ) : (
                            <span />
                        )}
                    </div>
                );
            },
        },
        {
            id: 'requests',
            header: 'Requests',
            accessorFn: (props) => {
                return <div>{props.fund_requests.length}</div>;
            },
        },
        {
            id: 'request_amount',
            header: 'Amount',
            accessorFn: (props) => {
                let totalAmount = 0;
                props.fund_requests.forEach((f) => {
                    // Ensure numeric addition even if API returns strings
                    const amt =
                        typeof f.request_amount === 'number'
                            ? f.request_amount
                            : Number(
                                  String(f.request_amount).replace(/[,\s]/g, '')
                              ) || 0;
                    totalAmount += amt;
                });
                return toUSD(totalAmount);
            },
        },
        {
            id: 'actions',
            header: '',
            accessorFn: (props) => {
                // Only show delete action if there are no fund requests
                const hasNoFundRequests = props.fund_requests.length === 0;

                if (!hasNoFundRequests) {
                    return <span />;
                }

                return (
                    <div>
                        <MdDelete
                            color='#dc3545'
                            size={20}
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(props);
                            }}
                            title='Delete payee'
                        />
                    </div>
                );
            },
        },
    ];

    return (
        <div>
            <TableContainer
                showHeaders
                columns={columns}
                data={payees}
                showPaginationComponents={false}
                showFilterComponents={false}
                NoDataCard={
                    <NoDataCard
                        title={'You don’t have any payees yet'}
                        body={
                            'This is where you will find people, organizations, or vendors that your fund requests will pay.'
                        }
                    />
                }
                isServerSidePagination={true}
                sort={false}
                page={currentPage}
                pageCount={totalPages}
                setPage={onPageChange}
                tableLoader={isLoading}
                setPageSize={setPageSize}
                pageSize={pageSize}
            />

            <ConfirmModal
                isOpen={isDeleteModalOpen}
                toggle={handleDeleteCancel}
                handleConfirm={handleDeleteConfirm}
                title='Delete Payee'
                message={`Are you sure you want to delete "${vendorToDelete?.name}"? This action cannot be undone.`}
                saving={isDeleting}
                isDeleting={true}
            />
        </div>
    );
}

export default PayeesTable;
